/**
 * Client-safe catalog of the fixed negotiation concessions offered to a venue.
 * `label` is the plain-language text shown in the staff checkbox grid and the
 * Slack summaries; `termLabel` is the short name a negotiation plan card and
 * the hotel email use ("Room rate"). The internal verbatim `wording` each
 * concession renders as in an id-only email lives in the server-only sibling
 * module ({@link "./negotiation-concessions.server"}) so it's never sent to or
 * received from the client, closing a prompt-injection vector.
 */

/**
 * The parts of the deal a concession can touch, in the order the playbook
 * ranks them when readiness ties: what the rooms cost first, perks last.
 */
export const NEGOTIATION_CONCESSION_CATEGORIES = [
  "rate",
  "fees",
  "rooms",
  "meeting_space",
  "food_beverage",
  "contract",
  "perks",
  "other",
] as const;

/** The part of the deal a concession touches. */
export type NegotiationConcessionCategory = (typeof NEGOTIATION_CONCESSION_CATEGORIES)[number];

export interface NegotiationConcession {
  /** Stable id, validated server-side against the wording map before use. */
  id: string;
  /** Plain-language checkbox label. */
  label: string;
  /** Short name of the term this concession changes, shown on plan cards and in the hotel email. */
  termLabel: string;
  category: NegotiationConcessionCategory;
}

const BASE_CONCESSIONS = [
  { id: "room_rate", label: "Lower group room rate", termLabel: "Room rate", category: "rate" },
  {
    id: "comp_rooms",
    label: "Complimentary rooms (1 per 35 paid rooms)",
    termLabel: "Comp rooms",
    category: "rooms",
  },
  {
    id: "resort_fee_waiver",
    label: "Waive resort/destination fee",
    termLabel: "Resort or destination fee",
    category: "fees",
  },
  { id: "suite_upgrades", label: "Complimentary suite upgrades (2)", termLabel: "Suite upgrades", category: "rooms" },
  {
    id: "meeting_room_rental_waiver",
    label: "Waive meeting room rental",
    termLabel: "Meeting room rental",
    category: "meeting_space",
  },
  {
    id: "complimentary_internet",
    label: "Complimentary wifi in meeting and guest rooms",
    termLabel: "Wifi",
    category: "perks",
  },
  {
    id: "welcome_drinks",
    label: "Complimentary non-alcoholic welcome beverage",
    termLabel: "Welcome beverage",
    category: "food_beverage",
  },
  { id: "spa_discount", label: "20% discount on spa services", termLabel: "Spa discount", category: "perks" },
  { id: "av_discount", label: "30% discount on AV equipment", termLabel: "AV discount", category: "meeting_space" },
  {
    id: "fb_discount",
    label: "20% off menu pricing, 50% off staff meals",
    termLabel: "F&B pricing",
    category: "food_beverage",
  },
  {
    id: "fb_minimum_reduction",
    label: "Lower food and beverage minimum",
    termLabel: "F&B minimum",
    category: "food_beverage",
  },
  { id: "planner_points", label: "Hotel planner points", termLabel: "Planner points", category: "perks" },
  { id: "attrition_slippage", label: "25% cumulative attrition allowed", termLabel: "Attrition", category: "contract" },
  {
    id: "group_rate_extension",
    label: "Extend group rate before/after event dates",
    termLabel: "Group rate before and after",
    category: "rate",
  },
  { id: "self_parking", label: "Complimentary self-parking", termLabel: "Self-parking", category: "perks" },
  { id: "valet", label: "Complimentary valet", termLabel: "Valet", category: "perks" },
  {
    id: "preplanning_room_nights",
    label: "Complimentary room nights for pre-planning visits",
    termLabel: "Planning-visit nights",
    category: "rooms",
  },
  {
    id: "welcome_amenities",
    label: "Complimentary welcome amenities",
    termLabel: "Welcome amenities",
    category: "perks",
  },
  { id: "vip_amenities", label: "VIP amenities", termLabel: "VIP amenities", category: "perks" },
  {
    id: "early_late_checkout",
    label: "Early check-in / late check-out (VIPs)",
    termLabel: "VIP early check-in and late checkout",
    category: "perks",
  },
  {
    id: "cancellation_policy",
    label: "Tiered, mutual cancellation scale",
    termLabel: "Cancellation",
    category: "contract",
  },
  { id: "deposit_terms", label: "Deposit of 25% at signing", termLabel: "Deposit", category: "contract" },
  {
    id: "cutoff_date",
    label: "Room block cutoff 14 days before arrival",
    termLabel: "Cutoff date",
    category: "contract",
  },
  { id: "gym_access", label: "Complimentary gym access", termLabel: "Gym access", category: "perks" },
  {
    id: "pool_access",
    label: "No pool renovations during event",
    termLabel: "No renovation during the event",
    category: "contract",
  },
] as const satisfies readonly NegotiationConcession[];

const EXPANDED_CONCESSIONS = [
  { id: "rate_match", label: "Match any lower public rate", termLabel: "Lowest rate match", category: "rate" },
  { id: "staff_rooms", label: "2 staff rooms at 50% off", termLabel: "Staff rooms", category: "rooms" },
  {
    id: "hospitality_suite",
    label: "Complimentary hospitality suite",
    termLabel: "Hospitality suite",
    category: "rooms",
  },
  {
    id: "comp_room_fees",
    label: "No resort fee or tax on comp rooms",
    termLabel: "Fees on comp rooms",
    category: "fees",
  },
  { id: "early_departure_fee", label: "Waive early departure fee", termLabel: "Early departure fee", category: "fees" },
  {
    id: "breakfast_included",
    label: "Breakfast included in the group rate",
    termLabel: "Breakfast",
    category: "rooms",
  },
  { id: "resale_credit", label: "Resold rooms count toward pickup", termLabel: "Resale credit", category: "contract" },
  {
    id: "pickup_counting",
    label: "Pre/post nights and comp rooms count toward pickup",
    termLabel: "Pickup counting",
    category: "contract",
  },
  {
    id: "block_review_date",
    label: "Block review date 60 days out",
    termLabel: "Block review date",
    category: "contract",
  },
  {
    id: "rebook_credit",
    label: "Credit cancellation fees on a rebook",
    termLabel: "Rebook credit",
    category: "contract",
  },
  { id: "no_walk", label: "No-walk clause", termLabel: "No-walk clause", category: "contract" },
  {
    id: "renovation_protection",
    label: "Renovation notice and remedy",
    termLabel: "Renovation notice",
    category: "contract",
  },
  {
    id: "space_reassignment",
    label: "No meeting space changes without consent",
    termLabel: "Meeting space changes",
    category: "contract",
  },
  { id: "non_compete", label: "No competing groups in house", termLabel: "Competing groups", category: "contract" },
  { id: "beo_price_lock", label: "Lock banquet menu pricing", termLabel: "Menu pricing", category: "contract" },
  { id: "payment_terms", label: "ACH payment, Net 30", termLabel: "Payment terms", category: "contract" },
  {
    id: "data_privacy",
    label: "Attendee data used only for this event",
    termLabel: "Attendee data",
    category: "contract",
  },
  {
    id: "weather_backup",
    label: "Indoor backup for outdoor functions",
    termLabel: "Weather backup",
    category: "contract",
  },
  {
    id: "space_hold_24h",
    label: "24-hour hold on meeting rooms",
    termLabel: "24-hour hold",
    category: "meeting_space",
  },
  {
    id: "setup_fees_waiver",
    label: "Waive setup and labor fees",
    termLabel: "Setup and labor fees",
    category: "meeting_space",
  },
  {
    id: "coffee_break_discount",
    label: "20% off coffee breaks",
    termLabel: "Coffee breaks",
    category: "food_beverage",
  },
  {
    id: "banquet_tasting",
    label: "Complimentary banquet tasting",
    termLabel: "Banquet tasting",
    category: "food_beverage",
  },
  {
    id: "fb_minimum_counting",
    label: "Service charge and tax count toward the F&B minimum",
    termLabel: "F&B minimum counting",
    category: "food_beverage",
  },
  {
    id: "reception_discount",
    label: "15% off the hosted reception",
    termLabel: "Hosted reception",
    category: "food_beverage",
  },
  {
    id: "outside_av",
    label: "Allow outside AV with no added fees",
    termLabel: "Outside AV",
    category: "meeting_space",
  },
  {
    id: "event_tech_fee_waiver",
    label: "Waive event technology fee",
    termLabel: "Event technology fee",
    category: "fees",
  },
  {
    id: "dedicated_bandwidth",
    label: "Dedicated bandwidth for sessions",
    termLabel: "Dedicated bandwidth",
    category: "meeting_space",
  },
  { id: "package_handling", label: "Waive package handling fees", termLabel: "Package handling", category: "perks" },
  { id: "signage", label: "Complimentary signage and easels", termLabel: "Signage", category: "perks" },
  { id: "airport_shuttle", label: "Complimentary airport shuttle", termLabel: "Airport shuttle", category: "perks" },
  {
    id: "registration_space",
    label: "Complimentary registration area",
    termLabel: "Registration space",
    category: "meeting_space",
  },
  { id: "room_drop_fees", label: "Waive room drop delivery fees", termLabel: "Room drops", category: "perks" },
  {
    id: "staff_parking",
    label: "Loading dock and 2 staff parking spots",
    termLabel: "Loading dock and staff parking",
    category: "perks",
  },
  { id: "commission", label: "10% commission on room revenue", termLabel: "Commission", category: "contract" },
  {
    id: "service_charge_reduction",
    label: "Lower the F&B service charge",
    termLabel: "Service charge",
    category: "fees",
  },
  {
    id: "support_room",
    label: "Complimentary breakout, storage or office room",
    termLabel: "Support room",
    category: "meeting_space",
  },
  {
    id: "rate_after_cutoff",
    label: "Group rate honored after the cutoff",
    termLabel: "Rate after cutoff",
    category: "contract",
  },
  {
    id: "audit_clause",
    label: "Rooms booked outside the block count toward pickup",
    termLabel: "Pickup audit",
    category: "contract",
  },
  {
    id: "free_cancellation",
    label: "No charge if cancelled 30+ days out",
    termLabel: "Free cancellation",
    category: "contract",
  },
  {
    id: "attrition_damages",
    label: "Attrition damages at lost profit, less resold rooms",
    termLabel: "Attrition damages",
    category: "contract",
  },
  {
    id: "revenue_rebate",
    label: "Revenue rebate to the master account",
    termLabel: "Revenue rebate",
    category: "rate",
  },
] as const satisfies readonly NegotiationConcession[];

/**
 * Every selectable negotiation concession, in checkbox-grid display order.
 * `as const` so {@link NegotiationConcessionId} can be derived from it —
 * the server-only wording map, the negotiation playbook, and
 * `generate-bump-draft.ts`'s local conditions map are all keyed by that type,
 * so an id added here without a matching entry there is a compile error
 * instead of a silent `undefined` rendering into a venue-facing prompt.
 */
export const NEGOTIATION_CONCESSIONS = [...BASE_CONCESSIONS, ...EXPANDED_CONCESSIONS] as const;

/**
 * The checklist the negotiate modal falls back to when no plan arrives, kept
 * short instead of listing the whole bank.
 */
export const FALLBACK_CHECKLIST_IDS: readonly NegotiationConcessionId[] = [
  "comp_rooms",
  "resort_fee_waiver",
  "suite_upgrades",
  "meeting_room_rental_waiver",
  "complimentary_internet",
  "welcome_drinks",
  "spa_discount",
  "av_discount",
  "fb_discount",
  "planner_points",
  "attrition_slippage",
  "group_rate_extension",
  "self_parking",
  "valet",
  "preplanning_room_nights",
  "welcome_amenities",
  "vip_amenities",
  "early_late_checkout",
  "cancellation_policy",
  "gym_access",
  "pool_access",
];

/** Every valid concession id, derived from {@link NEGOTIATION_CONCESSIONS} — the single source of truth for the id union. */
export type NegotiationConcessionId = (typeof NEGOTIATION_CONCESSIONS)[number]["id"];

/** An id from the expanded bank, which staff surfaces never pre-tick and the staff bump draft never lists. */
export type ExpandedConcessionId = (typeof EXPANDED_CONCESSIONS)[number]["id"];

/** An id the staff grids show: one from the base bank. */
export type StaffGridConcessionId = (typeof BASE_CONCESSIONS)[number]["id"];

/**
 * The concessions the staff grids (the admin composer) show and select, in grid order. The
 * expanded bank stays with the plan, whose gates decide when each term fits; a staff pick has
 * no gate, so it chooses from the base bank only.
 */
export const STAFF_GRID_CONCESSION_IDS: readonly StaffGridConcessionId[] = BASE_CONCESSIONS.map(
  (concession) => concession.id
);

/** Every valid concession id, for validating an untrusted list at a server-action boundary. */
export const NEGOTIATION_CONCESSION_IDS: ReadonlySet<string> = new Set(
  NEGOTIATION_CONCESSIONS.map((concession) => concession.id)
);

/** One catalog entry, with its literal id. */
export type NegotiationConcessionEntry = (typeof NEGOTIATION_CONCESSIONS)[number];

const CONCESSIONS_BY_ID: ReadonlyMap<string, NegotiationConcessionEntry> = new Map(
  NEGOTIATION_CONCESSIONS.map((concession) => [concession.id, concession])
);

/**
 * Narrows an untrusted string to a catalog id.
 *
 * @param id - Any string, e.g. from a request body.
 * @returns Whether `id` names a concession in {@link NEGOTIATION_CONCESSIONS}.
 */
export function isNegotiationConcessionId(id: string): id is NegotiationConcessionId {
  return CONCESSIONS_BY_ID.has(id);
}

/**
 * The catalog entry for a known id.
 *
 * @param id - A catalog id.
 * @returns The concession's labels and category.
 * @throws {Error} Only if `id` was cast past the type system and isn't in the catalog.
 */
export function getNegotiationConcession(id: NegotiationConcessionId): NegotiationConcessionEntry {
  const concession = CONCESSIONS_BY_ID.get(id);
  if (!concession) throw new Error(`Unknown concession id: ${id}`);
  return concession;
}

/** The catalog id of a free-form ask outside the bank. */
export const CUSTOM_REQUEST_ID = "custom_request";

/**
 * A request outside the bank, such as "late checkout for the whole group".
 * Kept out of {@link NEGOTIATION_CONCESSIONS} so the playbook's gates, the
 * bank counts, and the staff grids never offer it; its `termLabel` here is
 * only the fallback, since each custom ask carries the label the model wrote.
 */
const CUSTOM_REQUEST = {
  id: CUSTOM_REQUEST_ID,
  label: "Other request",
  termLabel: "Other request",
  category: "other",
} as const satisfies NegotiationConcession;

/** Longest label a custom request may carry. */
export const CUSTOM_REQUEST_LABEL_MAX = 60;

/** Most custom requests the model or the client adds to one plan or send. */
export const CUSTOM_REQUESTS_MAX = 2;

/**
 * Abuse bound on the asks an org's contract clauses add to one plan or send, on top of the plan's.
 * Every clause the org keeps becomes an ask, so this never cuts a real org's list (71 orgs with
 * clauses in production, at most 19 asks each); it only stops a clause list no org keeps.
 */
export const ORG_ASKS_MAX = 25;

/**
 * Abuse bound on the asks one "what matters most" note adds, on top of the plan's. A note adds as
 * many asks as the client writes; this only stops a note no one would type by hand.
 */
export const NOTE_ASKS_MAX = 25;

/**
 * Terms that never go on the client's ask list, from the model, a chip, the note, a manual pick or
 * the send: Nowadays never negotiates the service charge or how it counts toward the F&B minimum,
 * and the email asks for commission on its own, on the server, so the client never sees or
 * unticks it.
 */
export const UNLISTED_CONCESSION_IDS: ReadonlySet<string> = new Set<NegotiationConcessionId>([
  "service_charge_reduction",
  "fb_minimum_counting",
  "commission",
]);

/**
 * The asks on listed terms. An ask on an unlisted term ({@link UNLISTED_CONCESSION_IDS}) never
 * goes to a hotel, whichever path carried it.
 *
 * @param asks - Asks from any caller.
 * @returns The asks with every unlisted term left out.
 */
export function listedAsks<T extends { concessionId: string }>(asks: T[]): T[] {
  return asks.filter((ask) => !UNLISTED_CONCESSION_IDS.has(ask.concessionId));
}

/** Any id an ask can carry: a bank concession or a custom request. */
export type AskConcessionId = NegotiationConcessionId | typeof CUSTOM_REQUEST_ID;

/**
 * The part of the deal an ask touches; a custom request has no category of its own.
 *
 * @param id - A bank concession id or the custom request id.
 * @returns The concession's category, or "other" for a custom request.
 */
export function askCategory(id: AskConcessionId): NegotiationConcessionCategory {
  return id === CUSTOM_REQUEST_ID ? CUSTOM_REQUEST.category : getNegotiationConcession(id).category;
}

/**
 * The identity of one ask within a plan or a send. A bank concession appears
 * at most once, so its id is enough; custom requests share one id and differ
 * by label.
 *
 * @param ask - The ask's id and term label.
 * @returns The concession id, or `custom_request:` plus the lowercased label.
 */
export function askKey(ask: { concessionId: AskConcessionId; termLabel: string }): string {
  if (ask.concessionId !== CUSTOM_REQUEST_ID) return ask.concessionId;
  return `${CUSTOM_REQUEST_ID}:${ask.termLabel.trim().toLowerCase()}`;
}

/** How a money ask is counted. */
export type AskValueUnit = "per_night" | "per_room_night" | "total";

/**
 * One side of an ask. Money carries a number and the quote currency so the
 * client can format it; everything else is text ("1 per 35", "Waived").
 */
export type AskValue =
  | { kind: "money"; amount: number; currency: string; unit: AskValueUnit }
  | { kind: "text"; text: string };

/** Must-haves lead the email; nice-to-haves follow. */
export type AskPriority = "must" | "nice";

/** Where an ask sits, in the modal and the email alike: the must-haves, the nice-to-haves, or the contract protections. */
export type AskSection = "must" | "nice" | "contract";

/** The sections in the order the client and the hotel read them. */
export const ASK_SECTIONS: readonly AskSection[] = ["must", "nice", "contract"];

/**
 * The section an ask belongs to. Contract-category nice-to-haves get their
 * own, since hotels accept most of them at little cost.
 *
 * @param ask - The ask's priority and id.
 * @returns "must" for a must-have, "contract" for a contract protection, else "nice".
 */
export function askSection(ask: { priority: AskPriority; concessionId: AskConcessionId }): AskSection {
  if (ask.priority === "must") return "must";
  return askCategory(ask.concessionId) === "contract" ? "contract" : "nice";
}

/**
 * An ask the client chose to send, as it crosses to the send path. Holds only
 * hotel-facing fields: the plan's client-only guidance (`likely`, `why`,
 * `fallback`, `isLongShot`) has no slot here, so it can't reach the email.
 */
export interface SelectedAsk {
  concessionId: AskConcessionId;
  priority: AskPriority;
  /**
   * For a bank concession, re-derived server-side from the id, so the client's
   * copy is display-only. For a custom request, the label itself: untrusted,
   * sanitized and capped at {@link CUSTOM_REQUEST_LABEL_MAX}.
   */
  termLabel: string;
  current: AskValue | null;
  target: AskValue;
  /** Hotel-facing reason. Client-editable, so untrusted on the server. */
  hotelReason: string;
  /**
   * The last round already asked for it, so the email marks it as still open. Client-set like the
   * reason, and it only adds fixed wording.
   */
  isReask?: boolean;
}

/**
 * The part of an ask a client may send: everything but `current`. That is the
 * hotel's own term, so the send path reads it from the offer's quote instead
 * of trusting the client's copy.
 */
export type AskEdit = Omit<SelectedAsk, "current">;

/**
 * An ask as a request carries it: a full ask with its values, or just an id
 * for paths that only pick bank concessions (the manual fallback without a
 * plan), which the server words with the playbook's default. A custom request
 * always travels as a full ask, since its label is the ask.
 */
export type AskRequest = AskEdit | { concessionId: NegotiationConcessionId; priority: AskPriority };

/** What a negotiation send carries besides the note. */
export interface NegotiationSendRequest {
  asks: AskRequest[];
  /** The offer the asks are about; the email names it by its dates when the venue holds several. */
  quoteGroupId: string | null;
  /** Whether the asks came from a generated plan, recorded with the send. */
  fromPlan: boolean;
  /** The client lets the hotel read a target price worked out from its leverage; absent shares no figure. */
  shareBudget?: boolean;
}

/** Longest client-editable text on a sent ask (a text target or a hotel reason). */
export const SELECTED_ASK_FIELD_MAX = 160;

/**
 * Most asks a plan ticks, and most concession ids an id-only send (the Slack bot, the staff
 * composer) carries. A client send may carry more: see {@link SEND_ASKS_MAX}.
 */
export const SELECTED_ASKS_MAX = 15;

/**
 * The perks the client's "Perks" pick adds when the plan left them out and the deal can use them: the
 * freebies that matter most to a group.
 */
export const IMPORTANT_PERK_IDS: readonly NegotiationConcessionId[] = [
  "comp_rooms",
  "suite_upgrades",
  "preplanning_room_nights",
  "early_late_checkout",
  "group_rate_extension",
  "self_parking",
  "welcome_amenities",
  "vip_amenities",
];

/** Most asks one client send carries: a plan's worth, plus what the client's answer and the org's clauses added. */
export const SEND_ASKS_MAX = SELECTED_ASKS_MAX + IMPORTANT_PERK_IDS.length + NOTE_ASKS_MAX + ORG_ASKS_MAX;

/** Most must-haves an ask list leads with; hotels answer a short ranked list. */
export const MUST_ASKS_MAX = 7;

/** Most contract protections (contract-category nice-to-haves) one ask list carries. */
export const CONTRACT_ASKS_MAX = 8;

/** Minimum total room count for the `comp_rooms` concession to auto-select ("include only if total room count is >= 40"). */
export const COMP_ROOMS_MIN_ROOM_COUNT = 40;

/**
 * Event/quote signals the checkbox grid's auto-selection reads, mirroring the
 * conditions `generate-bump-draft.ts`'s `NEGOTIATION_CONCESSION_CONDITIONS`
 * describes to the model — kept in sync manually since one drives a
 * deterministic checkbox default and the other drives an LLM prompt.
 */
export interface ConcessionAutoSelectContext {
  numGuestrooms: number | null;
  numMeetingRooms: number | null;
  /** Whether the quote charges a resort or destination fee. */
  hasResortOrDestinationFee: boolean;
  /** Whether the quote already includes wifi. */
  hasComplimentaryInternetIncluded: boolean;
}

/**
 * Computes which concessions should start checked in the admin composer's
 * grid, given what's actually known about the event/venue/quote. Client-safe
 * (ids only, no wording) so it can run in the composer's initial state.
 * Concessions with no real signal to gate on ("when relevant" / "always
 * include" per the fixed list) default to checked.
 *
 * @param context - Room counts and quote-derived fee/amenity signals.
 * @returns The concession ids that should start checked.
 */
export function getAutoSelectedConcessionIds(context: ConcessionAutoSelectContext): NegotiationConcessionId[] {
  const guestrooms = context.numGuestrooms ?? 0;
  const meetingRooms = context.numMeetingRooms ?? 0;

  // Rate, F&B minimum, deposit and cutoff asks need quote numbers this context doesn't
  // carry, so they start unchecked and a person ticks them.
  const isAutoSelected: Record<StaffGridConcessionId, boolean> = {
    room_rate: false,
    fb_minimum_reduction: false,
    deposit_terms: false,
    cutoff_date: false,
    comp_rooms: guestrooms >= COMP_ROOMS_MIN_ROOM_COUNT,
    resort_fee_waiver: context.hasResortOrDestinationFee,
    suite_upgrades: guestrooms > 0,
    meeting_room_rental_waiver: meetingRooms > 0,
    complimentary_internet: !context.hasComplimentaryInternetIncluded,
    welcome_drinks: true,
    spa_discount: true,
    av_discount: meetingRooms > 0,
    fb_discount: meetingRooms === 0,
    planner_points: true,
    attrition_slippage: true,
    group_rate_extension: true,
    self_parking: true,
    valet: true,
    preplanning_room_nights: true,
    welcome_amenities: true,
    vip_amenities: true,
    early_late_checkout: true,
    cancellation_policy: true,
    gym_access: true,
    pool_access: true,
  };

  return BASE_CONCESSIONS.map((c) => c.id).filter((id) => isAutoSelected[id]);
}

/**
 * Renders selected concession ids as their client-safe labels, any custom requests, and the
 * note into one short, human-readable summary of what was asked for — shared by the Slack
 * "negotiation started"/"follow-up sent" notifications so the two don't drift in wording.
 *
 * @param concessionIds - Selected concession ids (unknown ids are silently dropped, same as the grid).
 * @param escapedNote - The note, already Slack-escaped by the caller (e.g. via `escapeSlackText`) —
 *   this function does no escaping of its own since it doesn't know the target surface.
 * @param escapedCustomLabels - Custom requests' labels, client-written and already escaped by the caller,
 *   listed after the bank labels.
 * @returns A summary like `"asked for: Complimentary valet, Waive resort/destination fee; note: \"...\""`,
 *   or `"no specific asks selected"` when everything is empty.
 */
export function formatNegotiationAsk(
  concessionIds: string[],
  escapedNote: string | null,
  escapedCustomLabels: string[] = []
): string {
  const labels = [
    ...NEGOTIATION_CONCESSIONS.filter((c) => concessionIds.includes(c.id)).map((c) => c.label),
    ...escapedCustomLabels,
  ];
  const parts: string[] = [];
  if (labels.length > 0) parts.push(`asked for: ${labels.join(", ")}`);
  if (escapedNote) parts.push(`note: "${escapedNote}"`);
  return parts.length > 0 ? parts.join("; ") : "no specific asks selected";
}
